/**
 * AgentOrdersTab — "My Orders" tab body extracted from the monolithic
 * PropertyAdminDashboard.
 *
 * Groups the agent's inbound orders by their assigned property, shows
 * a fallback "Unassigned" group last, and lets the agent re-assign
 * each order to any of their linked properties via a per-row select.
 */
import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { getApiUrl } from "@/utils/api";
import { Loader2, ShoppingBag, Printer, Mail, CheckCircle2, Clock } from "lucide-react";

const API = getApiUrl();

export default function AgentOrdersTab({
  orders,
  ordersLoading,
  linkedProperties,
  setOrders,
  filterPropertyId = null,
}) {
  const [notifyingId, setNotifyingId] = useState(null);

  const notifySupplier = async (orderId) => {
    setNotifyingId(orderId);
    try {
      const { data } = await axios.post(
        `${API}/property-admin/me/orders/${orderId}/notify-supplier`,
        {},
        { withCredentials: true },
      );
      setOrders((prev) =>
        prev.map((o) =>
          o.order_id === orderId
            ? {
                ...o,
                supplier_notified_at: data.notified_at,
                supplier_notified_suppliers: data.sent,
                supplier_notifications: data.supplier_notifications,
              }
            : o,
        ),
      );
      toast.success(`Instruction emailed to ${data.sent.join(", ")}`);
      if (data.failed?.length) {
        toast.error(`Failed to email: ${data.failed.join(", ")}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to notify supplier");
    } finally {
      setNotifyingId(null);
    }
  };

  const assignOrderProperty = async (orderId, newPropId) => {
    try {
      await axios.put(
        `${API}/property-admin/me/orders/${orderId}/assign-property`,
        { property_id: newPropId || null },
        { withCredentials: true },
      );
      setOrders((prev) =>
        prev.map((o) =>
          o.order_id === orderId
            ? { ...o, assigned_property_id: newPropId || null }
            : o,
        ),
      );
      toast.success(
        newPropId ? "Order assigned to property" : "Order unassigned",
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to assign order");
    }
  };

  return (
    <div className="bg-white border border-border p-6" data-testid="orders-tab-content">
      {!filterPropertyId && (
        <>
          <h2 className="text-xl font-semibold flex items-center gap-2 mb-1">
            <ShoppingBag className="h-5 w-5" /> My Orders
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            Every order placed with your account email, grouped by the property they were assigned to. Change an order&apos;s assigned property from the row dropdown.
          </p>
        </>
      )}
      {ordersLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : orders.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-8 text-center text-sm text-muted-foreground bg-muted/20" data-testid="orders-empty">
          No orders yet.
        </div>
      ) : (
        (() => {
          // Group orders by assigned_property_id; "Unassigned" last.
          const propTitle = (pid) => {
            const p = linkedProperties.find((x) => x.property_id === pid);
            return p?.title || p?.display_address || pid;
          };
          const productCount = (ordersArr) =>
            ordersArr.reduce(
              (sum, o) => sum + (o.items || []).reduce((s, it) => s + (it.quantity || 1), 0),
              0,
            );
          const groups = new Map();
          for (const o of orders) {
            const key = o.assigned_property_id || "";
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push(o);
          }
          let orderedKeys = [
            ...linkedProperties
              .map((p) => p.property_id)
              .filter((pid) => groups.has(pid)),
            ...Array.from(groups.keys()).filter(
              (k) => k && !linkedProperties.find((p) => p.property_id === k),
            ),
          ];
          if (groups.has("")) orderedKeys.push(""); // unassigned last
          // Card CTA — "Orders about this property" — narrows to just that
          // property's group instead of the full cross-property view.
          if (filterPropertyId) {
            orderedKeys = orderedKeys.filter((k) => k === filterPropertyId);
          }

          if (orderedKeys.length === 0) {
            return (
              <div className="border border-dashed border-border rounded-lg p-8 text-center text-sm text-muted-foreground bg-muted/20" data-testid="orders-empty-filtered">
                No orders yet for this property.
              </div>
            );
          }

          return (
            <div className="space-y-6" data-testid="orders-list">
              {orderedKeys.map((key) => (
                <div
                  key={key || "unassigned"}
                  className="border border-border rounded overflow-x-auto"
                  data-testid={`orders-group-${key || "unassigned"}`}
                >
                  <div className="px-3 py-2 bg-muted/30 text-xs font-semibold uppercase tracking-wide flex items-center justify-between">
                    <span>{key ? propTitle(key) : "Unassigned"}</span>
                    <span className="text-muted-foreground normal-case font-normal" data-testid={`orders-group-summary-${key || "unassigned"}`}>
                      {groups.get(key).length} order
                      {groups.get(key).length === 1 ? "" : "s"} ·{" "}
                      {productCount(groups.get(key))} product
                      {productCount(groups.get(key)) === 1 ? "" : "s"} bought
                    </span>
                  </div>
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide">
                      <tr>
                        <th className="p-3">Placed</th>
                        <th className="p-3">Order</th>
                        <th className="p-3">Items</th>
                        <th className="p-3">Total</th>
                        <th className="p-3">Status</th>
                        <th className="p-3">Assign to</th>
                        <th className="p-3 text-right"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {groups.get(key).map((o) => {
                        const symbol = { GBP: "£", USD: "$", EUR: "€" }[o.currency] || "";
                        const status = o.payment_status || o.status || "pending";
                        const badgeCls = status === "paid"
                          ? "bg-green-100 text-green-700"
                          : status === "failed"
                            ? "bg-red-100 text-red-700"
                            : "bg-amber-100 text-amber-700";
                        return (
                          <tr key={o.order_id} className="border-t border-border align-top" data-testid={`order-row-${o.order_id}`}>
                            <td className="p-3 text-xs whitespace-nowrap">{new Date(o.created_at).toLocaleString()}</td>
                            <td className="p-3 font-mono text-xs">#{(o.order_id || "").slice(0, 8).toUpperCase()}</td>
                            <td className="p-3">
                              {(o.items || []).map((it, i) => (
                                <div key={i} className="text-xs">{it.quantity} × {it.name}</div>
                              ))}
                            </td>
                            <td className="p-3 whitespace-nowrap font-medium">{symbol}{Number(o.total || 0).toFixed(2)}</td>
                            <td className="p-3">
                              <span className={`text-xs px-2 py-0.5 rounded ${badgeCls} capitalize`}>{status}</span>
                            </td>
                            <td className="p-3">
                              <select
                                value={o.assigned_property_id || ""}
                                onChange={(e) => assignOrderProperty(o.order_id, e.target.value)}
                                className="text-xs border border-input rounded px-2 py-1 bg-background min-w-[140px]"
                                data-testid={`order-assign-select-${o.order_id}`}
                              >
                                <option value="">Unassigned</option>
                                {linkedProperties.map((p) => (
                                  <option key={p.property_id} value={p.property_id}>
                                    {p.title || p.display_address || p.property_id}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="p-3 text-right">
                              <div className="flex flex-col items-end gap-1.5">
                                {(o.supplier_notifications || []).length > 0 && (
                                  <div
                                    className="flex flex-wrap justify-end gap-1.5"
                                    data-testid={`order-supplier-ack-list-${o.order_id}`}
                                  >
                                    {o.supplier_notifications.map((n) => (
                                      <span
                                        key={n.supplier_id}
                                        className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-full border ${
                                          n.acknowledged_at
                                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                            : "bg-amber-50 text-amber-700 border-amber-200"
                                        }`}
                                        title={
                                          n.acknowledged_at
                                            ? `Acknowledged ${new Date(n.acknowledged_at).toLocaleString()}`
                                            : `Sent ${new Date(n.sent_at).toLocaleString()} — awaiting acknowledgement`
                                        }
                                        data-testid={`order-supplier-ack-${o.order_id}-${n.supplier_id}`}
                                      >
                                        {n.acknowledged_at ? (
                                          <CheckCircle2 className="h-3 w-3" />
                                        ) : (
                                          <Clock className="h-3 w-3" />
                                        )}
                                        {n.supplier_name}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                <div className="flex items-center justify-end gap-3">
                                  <button
                                    type="button"
                                    onClick={() => notifySupplier(o.order_id)}
                                    disabled={notifyingId === o.order_id}
                                    className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline disabled:opacity-50"
                                    data-testid={`order-notify-supplier-${o.order_id}`}
                                  >
                                    {notifyingId === o.order_id ? (
                                      <Loader2 className="h-3 w-3 animate-spin" />
                                    ) : (
                                      <Mail className="h-3 w-3" />
                                    )}
                                    {o.supplier_notified_at ? "Resend" : "Notify Supplier"}
                                  </button>
                                  <Link
                                    to={`/my-orders/${o.order_id}`}
                                    className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                                    data-testid={`order-view-${o.order_id}`}
                                  >
                                    <Printer className="h-3 w-3" /> View / Print
                                  </Link>
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          );
        })()
      )}
    </div>
  );
}
